// ============================================================
// ShopDrawings.js
// Shop drawing list + editor, persisted to the `shopDrawings` collection.
//
// Two views on one route: a list of saved drawings (default) and an editor
// with a live preview of the sheet.
//
// ONLY THE INPUTS ARE STORED. No computed cut lengths, no SVG markup — the
// drawing is regenerated from these fields every time it is opened, so a change
// to a fabrication rule reflows every existing drawing instead of leaving
// stale geometry baked into the record.
//
// `revision` and `status` are written as groundwork for a future approval flow;
// there is deliberately no UI for them yet.
// ============================================================

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import GateSheet, { PLATE_TYPE } from '../components/GateSheet';
import { computeGateCuts, formatInches, formatFeetInches, SHORT_GATE_MAX_HEIGHT_IN } from '../utils/gateDrawing';
import {
  POST_OD_OPTIONS, PLATE_SIZE_OPTIONS, THICKNESS_OPTIONS, PLATE_POSITIONS, BLANK_PLATE,
  MAX_PLATES_PER_SHEET, optionsFor, plateSizeLabel, thicknessLabel, postOdLabel, normalizePlatePosition,
  POST_LENGTH_MAX, plateForForm,
} from '../components/PlateDetail';
import { generateShopDrawingNumber } from '../utils/docNumber';
import { getAllPhoneNumbers } from '../utils/customerPhone';
import { useData } from '../context/DataContext';
import { useRole } from '../hooks/useRole';
import { logDeletion } from '../utils/deletionLog';
import { shopStatusFields, isShopStatus, resolveShopStatus, SHOP_STATUS_KEYS } from '../utils/shopStatus';
import { normalizeSearch, canonicalDocNumbers } from '../utils/searchMatch';

const C_BG = '#F4F8FC';
const C_CARD = '#FFFFFF';
const C_BORDER = '#E3ECF5';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_ACCENT = '#0077B6';
const C_TINT = '#E6F1FB';

// ── MESH GAUGE BY GRADE ──────────────────────────────────────────────
// The gauge the shop uses unless somebody says otherwise. A STARTING VALUE,
// not a constraint: the field stays freely editable in every state.
const MESH_GAUGE_BY_GRADE = {
  Commercial: '9',
  Residential: '11.5',
};
const DEFAULT_GAUGE_VALUES = Object.values(MESH_GAUGE_BY_GRADE).map(parseFloat);

// Is the gauge currently showing one of the two defaults?
//
// Compared NUMERICALLY, so "9.0" and " 9 " count as the commercial default —
// they are the same gauge, typed differently, and treating them as bespoke
// would strand somebody who retyped the value they already had.
//
// An empty field is NOT a default. Somebody who cleared it is mid-edit, and
// filling it back in under them while they change the grade is the same
// surprise this whole rule exists to avoid.
const isDefaultGauge = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && DEFAULT_GAUGE_VALUES.some(d => Math.abs(n - d) < 1e-9);
};

// ── OVERALL LENGTH OVERRIDE (cantilever) ─────────────────────────────
// The form holds what was typed, as a string; '' means "not overridden — print
// the formula's total". Only a positive number is an override. Anything else
// (0, a stray minus) is not, so the sheet keeps the formula's figure — and the
// form says so rather than quietly ignoring it.
//
// A LABEL ONLY. It is handed to computeGateCuts, which prints it and uses it
// for nothing else; the frame, bays, mesh and cuts stay on the formula.
const overallOverrideIn = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// ── WHEN TO WARN: THE FORMULA MOVED, NOT "THE OVERRIDE DIFFERS" ──
//
// Every real override differs from the formula — that is what makes it an
// override — so warning on the difference put a warning permanently on screen,
// and a warning that is always there is one nobody reads.
//
// What the owner needs to hear is narrower: "you typed a number, and since then
// the formula it was typed against has changed." So the formula's value is
// captured at the moment the override is typed, as overallLengthBaseIn, and
// the warning fires only while the CURRENT formula differs from that base.
// Change the opening and it fires; change the opening back and it clears.
//
// The base is form bookkeeping. It is never handed to computeGateCuts, so it
// cannot reach the printed sheet.
//
// The formula's overall length (opening + counterbalance) for a set of form
// values, to the cent — from computeGateCuts, the engine the sheet itself uses.
const formulaOverallIn = (f) =>
  Math.round(computeGateCuts({ type: f.type, openingIn: parseFloat(f.openingIn) || 0 }).totalLengthIn * 100) / 100;
const sameToTheCent = (a, b) => Math.round(a * 100) === Math.round(b * 100);

// Spelled once and used for both fields below, so a new drawing's grade and its
// gauge cannot drift apart the day one of them is changed.
const BLANK_GRADE = 'Residential';

const BLANK = {
  type: 'Single Swing',
  // POST PLATES only. Empty for every gate drawing, and never written to one.
  plates: [],
  grade: BLANK_GRADE,
  openingIn: '48',
  heightIn: '48',
  quantity: '1',
  barbedWire: false,
  actualSize: false,
  frameOnly: false,
  nailers2x4: false,
  // Wood is the common case, so the mid rail's board starts on.
  nailerMidRail: true,
  // '' = use the rule. Saved as null in that case, so an un-overridden drawing
  // stores nothing new.
  horizontalRailCount: '',
  verticalMemberCount: '',
  fullHeight: false,
  blankSheet: false,
  customerCopy: false,
  slideDirection: 'Right',
  hangSide: 'Outside',
  operation: 'Manual',
  // '' = not overridden: print the computed overall length. Saved as null.
  overallLengthIn: '',
  // The formula's total when the override was typed; '' whenever there is no
  // override. Saved as null in that case.
  overallLengthBaseIn: '',
  frameFinish: 'Galvanized',
  meshFinish: 'Galvanized',
  meshGauge: MESH_GAUGE_BY_GRADE[BLANK_GRADE],
  customerId: null,
  customerName: '',
  documentRef: '',
  // The REAL link. documentRef above stays exactly as it was — free text on
  // older records that cannot be resolved and is deliberately not migrated.
  linkedDocId: null,
  linkedDocType: null,
  linkedDocNumber: '',
};

const cardStyle = { background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 16, padding: 20 };
const sectionTitle = { color: C_TEXT, fontWeight: 700, fontSize: 15, margin: '0 0 12px 0' };
const labelStyle = { display: 'block', color: C_TEXT, fontWeight: 600, fontSize: 13, marginBottom: 4 };
const fieldStyle = {
  width: '100%', border: `1px solid ${C_BORDER}`, borderRadius: 10, padding: '8px 12px',
  fontSize: 14, color: C_TEXT, background: C_CARD, outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s',
};
const readOnlyStyle = { ...fieldStyle, background: C_BG, color: C_TEXT_2, cursor: 'default' };
// The search box copies the treatment Documents and Customers already use for
// theirs: the page's own field style, opened out to the same 12x16 padding,
// with room on the left for the magnifier glyph.
const searchInputStyle = { ...fieldStyle, padding: '12px 16px', paddingLeft: 44, background: C_CARD };
// Drawing number and Drawn by are assigned by the app, never typed. They sit in
// the Customer block as REFERENCE, so they are set a size down and greyed back
// from the editable fields around them — the eye should skip them rather than
// stop to fill them in.
const refLabelStyle = { ...labelStyle, fontSize: 11, color: C_TEXT_2 };
const refFieldStyle = { ...readOnlyStyle, padding: '6px 10px', fontSize: 12 };
// ── List search ──
//
// Everything is matched CLIENT-SIDE against the already-loaded list. Firestore
// has no substring or case-insensitive operator, so a server-side text search
// would mean a field-per-query design plus indexes, for a list that is already
// sitting in memory.

// Strip everything that is not a letter or a digit, and lowercase. This is what
// makes punctuation irrelevant on both sides of the comparison, so "sd260001",
// "SD-26-0001" and "sd 26 0001" are all the same string to the matcher.
// normalizeSearch lives in utils/searchMatch.js — one copy, shared with the
// Dashboard board search and the Documents page.

// The fields a query is tested against — all of them at once, so the user never
// has to pick which one they are searching by.
const searchableFields = (d) => [
  d?.drawingNumber, d?.customerName, d?.documentRef, d?.type, d?.drawnBy,
  // Fabrication status, so "pending" or "complete" narrows the list. Older
  // records have no field and are Pending, so the fallback matters here too.
  d?.shopStatus || 'Pending',
];

/**
 * Does this drawing match the query?
 *
 * Three rules, in increasing forgiveness, and a drawing matching ANY of them is
 * a hit:
 *
 *   1. Plain case-insensitive substring on the raw field values. This is what
 *      keeps a partial like "26-00" working, punctuation and all.
 *   2. The same test with all punctuation stripped from BOTH sides, so the
 *      hyphens in a drawing number stop mattering: "sd260001" finds SD-26-0001.
 *   3. For an all-digits query, the drawing number's trailing sequence compared
 *      NUMERICALLY, which is what lets "1" find SD-26-0001 without the user
 *      having to type the leading zeros.
 *
 * An empty or whitespace-only query matches everything.
 */
export function drawingMatchesSearch(drawing, rawQuery) {
  const trimmed = String(rawQuery ?? '').trim();
  if (!trimmed) return true;

  const fields = searchableFields(drawing);

  // 1. Raw substring.
  const lower = trimmed.toLowerCase();
  if (fields.some(f => String(f ?? '').toLowerCase().includes(lower))) return true;

  // 2. Punctuation-insensitive substring.
  const nq = normalizeSearch(trimmed);
  if (nq && fields.some(f => normalizeSearch(f).includes(nq))) return true;

  // 3. Sequence number, leading zeros ignored.
  if (/^\d+$/.test(trimmed)) {
    const sequence = String(drawing?.drawingNumber ?? '').split('-').pop();
    if (/^\d+$/.test(sequence) && parseInt(sequence, 10) === parseInt(trimmed, 10)) return true;
  }

  return false;
}

/**
 * A gate quantity is a count of physical gates: whole, and at least one. Zero,
 * negatives and fractions are meaningless, so they are clamped here rather than
 * being allowed to reach the engine or Firestore. Anything unparseable — an
 * emptied field mid-typing included — falls back to 1.
 */
// Fabrication status, as set by the shop on /shop. READ ONLY here — the office
// sees what the welders have done without having to ask, and cannot change it
// from this screen. Older drawings have no shopStatus field at all and read as
// Pending, which is what they are.
// KEY vs LABEL — they deliberately differ.
//
// The KEY is the value STORED in Firestore. The LABEL is only what a screen
// says. Renaming a key would mean migrating every record already written, and
// until that migration finished every older document would resolve to nothing
// and silently show the fallback — so the stored vocabulary is frozen and the
// wording is changed here, where it costs nothing.
//
// So 'Pending' is the stored value and "Submitted" is what everyone reads. This
// map is a second copy of the one in Shop.js — the office only reads the status
// and the shop only writes it — so the two must be changed together.
const SHOP_STATUS = {
  'Pending': { label: 'Submitted', icon: '○', bg: '#FDEAEA', fg: '#8C2020' },
  'In Progress': { label: 'In Progress', icon: '◐', bg: '#FFF3DC', fg: '#7A4E00' },
  'Complete': { label: 'Complete', icon: '●', bg: '#E4F5EC', fg: '#0B5B44' },
};
const shopStatusOf = (d) => SHOP_STATUS[d?.shopStatus] || SHOP_STATUS.Pending;

// An overridden member count on the way to Firestore: a whole number, or null
// for "use the rule" — which is what an empty field means and what every drawing
// saved before overrides existed stores by having no field at all.
const countOverrideForSave = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
};

const clampQuantity = (value) => {
  const n = Math.round(parseFloat(value));
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

const focusOn = (e) => { e.target.style.borderColor = C_ACCENT; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; };
const focusOff = (e) => { e.target.style.borderColor = C_BORDER; e.target.style.boxShadow = 'none'; };
const btnPrimary = {
  background: C_ACCENT, border: 'none', color: '#FFFFFF', padding: '10px 20px', borderRadius: 10,
  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
const btnPlain = {
  background: C_CARD, border: `1px solid ${C_BORDER}`, color: C_TEXT, padding: '10px 20px',
  borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
// Destructive, and deliberately QUIETER than Save or Print: outlined rather
// than filled, in the app's red, and lighter in weight. Delete is rare and
// permanent — it should take a moment's aim, not sit there inviting a thumb.
// The red arrives when the pointer does.
const C_DANGER = '#A32D2D';
const C_DANGER_TINT = '#FBECEC';
const btnDanger = {
  background: 'transparent', border: `1px solid ${C_DANGER}40`, color: C_DANGER,
  padding: '10px 16px', borderRadius: 10,
  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

// ── FINDING A QUOTE OR AN INVOICE BY NUMBER ──────────────────────────
// canonicalDocNumbers now lives in utils/searchMatch.js, so the Documents
// page reads a document number exactly the way this lookup does. See the
// comment there for the forms it accepts and why it canonicalises rather
// than searches.

// Firestore Timestamp → Date. Immediately after a write the field can still be
// null (the server value has not resolved yet), so callers fall back.
const toDate = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : null);
const fmtDate = (d) => (d ? d.toLocaleDateString('en-US') : '');
const fmtTime = (d) => (d ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase() : '');
const normalizePhone = (s) => (s || '').replace(/\D/g, '');

export default function ShopDrawings({ user }) {
  // Same derivation NewDocument uses for `salesperson`.
  const drawnBy = user?.email?.split('@')[0]?.toUpperCase() || '';
  const { customers } = useData();
  const location = useLocation();
  const navigate = useNavigate();

  const { isAdmin, userName } = useRole();

  const [view, setView] = useState('list');
  const [drawings, setDrawings] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The saved record being edited (null while the drawing is new).
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(BLANK);
  // What the overall length box shows WHILE it has focus. Without it, emptying
  // the box would snap the computed figure straight back in mid-edit; with it,
  // the box can sit empty until the user types or leaves. null = not editing.
  const [overallDraft, setOverallDraft] = useState(null);
  // Single funnel for every form change, so the one cross-field invariant lives
  // in one place instead of in each control's handler. Full height is offered
  // only on a short swing gate that is not actual size; raising the height past
  // the threshold, ticking actual size, or switching to a cantilever all hide
  // the checkbox, and the flag has to go with it rather than stay silently true
  // where the user can no longer see or reach it.
  const set = (key, value) => setForm(f => {
    const next = { ...f, [key]: value };

    // ── Gauge follows grade, but ONLY OVER A DEFAULT ──
    //
    // ⚠️ DO NOT SIMPLIFY THIS INTO AN UNCONDITIONAL OVERWRITE. It reads like
    // one line of pointless caution and it is not.
    //
    // Changing the grade moves the gauge to that grade's default — but only if
    // the gauge is still SITTING ON a default. If somebody deliberately entered
    // 6 gauge, correcting the grade afterwards must not silently throw their 6
    // away: they would have to notice a field they already filled in had
    // changed itself, on a sheet that then goes to the shop and gets built.
    // Losing a typed value is much worse than leaving a stale default.
    //
    // The condition is checked against the gauge as it stands, so a value the
    // user typed survives any number of grade changes — including switching
    // away and back, which an unconditional version would quietly "fix".
    //
    // Applies to cantilevers too: the grade selector is shown for them, so the
    // default has to follow it there for the same reason it does anywhere else.
    //
    // This is the CHANGE path only. Loading a saved drawing goes through
    // setForm directly (see openExisting), never through here, so a stored
    // gauge is never rewritten on open whatever its grade says.
    if (key === 'grade' && isDefaultGauge(next.meshGauge) && MESH_GAUGE_BY_GRADE[value]) {
      next.meshGauge = MESH_GAUGE_BY_GRADE[value];
    }

    if (
      next.type === 'Cantilever'
      || next.actualSize
      || (parseFloat(next.heightIn) || 0) > SHORT_GATE_MAX_HEIGHT_IN
    ) {
      next.fullHeight = false;
    }

    // ── The override's base follows the override ──
    // Typing an override records the formula as it stands at that moment —
    // including typing the formula's own number, which is still an override and
    // gets that same value as its base. Clearing the box, resetting, or typing
    // something that is not a length leaves no override, so no base either.
    //
    // Only THIS key moves the base. Changing the opening leaves it exactly where
    // it was, which is the whole point: that is how the form knows the formula
    // has moved since the number was typed.
    if (key === 'overallLengthIn') {
      next.overallLengthBaseIn = overallOverrideIn(value) !== null ? String(formulaOverallIn(next)) : '';
    }
    return next;
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // ── Linking to a quote or invoice ──
  // What the user has typed, the candidates found for it, and whether a lookup
  // is in flight. `docLookup.state` drives the message under the field:
  //   'idle' | 'searching' | 'found' | 'none'
  const [docSearch, setDocSearch] = useState('');
  const [docLookup, setDocLookup] = useState({ state: 'idle', matches: [] });
  // Every lookup carries a token; a result whose token is stale is discarded.
  // Without it a slow query for "125" can land after a fast one for "1256" and
  // overwrite the right answer with the wrong one.
  const lookupToken = useRef(0);

  // ── Permanent delete ──
  // The drawing pending confirmation, or null. Holding the RECORD rather than
  // an id is what lets the confirmation name the drawing and the customer
  // without a second lookup.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Shop status, from the office ──
  //
  // The shop tablet owns this day to day; the office changes it occasionally —
  // a drawing marked Complete by mistake, or one the shop finished without
  // touching the tablet. So the control here is deliberately quiet: a small
  // select, not the tablet's big status buttons.
  //
  // ADMIN ONLY, re-checked HERE and not only where the control is drawn. The
  // JSX guard decides what a person SEES; this decides what the app WRITES, and
  // a handler reached any other way — a stale render, a future caller, the
  // console — must refuse just the same. (It is still a UI-level guard: the repo
  // carries no Firestore rules, so this stops a mistake, not an attacker.)
  //
  // No confirmation: it is a status, not a deletion, and setting it back is one
  // more click.
  const [statusSavingId, setStatusSavingId] = useState(null);
  const changeShopStatus = useCallback(async (drawing, status) => {
    if (!isAdmin) return;
    if (!drawing?.id || !isShopStatus(status)) return;
    if (resolveShopStatus(drawing) === status) return;   // nothing to write
    setStatusSavingId(drawing.id);
    try {
      // The SAME three fields the shop tablet writes, through the same helper.
      // shopStatusBy therefore carries the OFFICE user's name, so the shop can
      // see who moved it.
      const fields = shopStatusFields(userName, status);
      await updateDoc(doc(db, 'shopDrawings', drawing.id), fields);
      // This list is a one-shot read (not a listener like the tablet's), so the
      // row is patched here. The tablet needs nothing: it is subscribed to this
      // collection and the write reaches it on its own.
      setDrawings(prev => prev.map(d => (d.id === drawing.id ? { ...d, ...fields } : d)));
      setRecord(prev => (prev && prev.id === drawing.id ? { ...prev, ...fields } : prev));
    } catch (err) {
      console.error('Could not update the shop status:', err);
      alert('Could not save the shop status. Check the connection and try again.');
    }
    setStatusSavingId(null);
  }, [isAdmin, userName]);

  const loadDrawings = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'shopDrawings'), orderBy('createdAt', 'desc')));
      setDrawings(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading shop drawings:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDrawings(); }, [loadDrawings]);

  // ── Look the typed number up, in BOTH collections ──
  //
  // Quotes and invoices have separate counters, so the SAME number can exist as
  // both. Neither is more likely to be the one meant, so both are offered and
  // the user picks — guessing here would attach a drawing to the wrong document
  // and nothing on either screen would say so.
  //
  // Debounced, because this is a network round trip on every keystroke
  // otherwise. Three digits minimum: fewer cannot identify a document and would
  // just be two wasted queries.
  useEffect(() => {
    const raw = docSearch.trim();
    if (!raw) { setDocLookup({ state: 'idle', matches: [] }); return undefined; }
    if (raw.replace(/\D/g, '').length < 3) { setDocLookup({ state: 'idle', matches: [] }); return undefined; }

    const token = ++lookupToken.current;
    setDocLookup({ state: 'searching', matches: [] });
    const timer = setTimeout(async () => {
      const candidates = canonicalDocNumbers(raw);
      if (candidates.length === 0) {
        if (lookupToken.current === token) setDocLookup({ state: 'none', matches: [] });
        return;
      }
      try {
        // One EQUALITY query per (collection, candidate). Single field, so the
        // automatic index covers it and nothing has to be deployed.
        const jobs = [];
        candidates.forEach(num => {
          ['quote', 'invoice'].forEach(kind => {
            jobs.push(getDocs(query(
              collection(db, kind === 'quote' ? 'quotes' : 'invoices'),
              where('docNumber', '==', num),
            )).then(snap => snap.docs.map(d => ({
              id: d.id, docType: kind, docNumber: num, ...d.data(),
            }))));
          });
        });
        const found = (await Promise.all(jobs)).flat();
        if (lookupToken.current !== token) return;   // a later keystroke won
        setDocLookup({ state: found.length ? 'found' : 'none', matches: found });
      } catch (err) {
        console.error('Error looking up the document:', err);
        if (lookupToken.current === token) setDocLookup({ state: 'none', matches: [] });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [docSearch]);

  // Attach a found document. The three fields always move together.
  const linkDocument = (match) => {
    setForm(f => ({
      ...f,
      linkedDocId: match.id,
      linkedDocType: match.docType,
      linkedDocNumber: match.docNumber,
    }));
    setDocSearch('');
    setDocLookup({ state: 'idle', matches: [] });
  };

  // ALL THREE CLEARED TOGETHER. A stale id beside a blank number is a link that
  // looks absent and behaves present — the board would still count the drawing
  // against a document the user believes they detached.
  const unlinkDocument = () => {
    setForm(f => ({ ...f, linkedDocId: null, linkedDocType: null, linkedDocNumber: '' }));
    setDocSearch('');
    setDocLookup({ state: 'idle', matches: [] });
  };

  // ── PERMANENT DELETE ─────────────────────────────────────────────────
  //
  // No archive, no grace period, no restore. A confirmed delete removes the
  // document and that is the end of it.
  //
  // ADMIN ONLY, CHECKED TWICE. The control is hidden from anyone who is not an
  // admin, and the handler checks again before it writes. Hiding a button is an
  // affordance — it stops a colleague clicking in. It is not a rule: the handler
  // is reachable from a console, and the check that matters is the one next to
  // the write. In practice every office account is admin and the shop role never
  // reaches this page at all, but neither of those is a reason to trust the
  // routing instead of the check.
  //
  // ⚠️ THE COUNTER IS NOT TOUCHED. Drawing numbers come from a transaction on
  // counters/shopDrawingCounter that only ever increments. Deleting SD-26-0007
  // deletes the drawing and nothing else — the next one is still SD-26-0008,
  // exactly as invoices behave. Rolling the counter back would hand a fresh
  // drawing a number that already appeared on a printed sheet in somebody's
  // truck, which is far worse than a gap in a sequence.
  const requestDelete = useCallback((d) => {
    if (!isAdmin || !d) return;
    setDeleteTarget(d);
  }, [isAdmin]);

  const confirmDelete = async () => {
    // The second check. See the note above.
    if (!isAdmin || !deleteTarget) { setDeleteTarget(null); return; }
    setDeleting(true);
    try {
      // ── THE LOG IS WRITTEN FIRST, AND THE DELETE DEPENDS ON IT ──
      //
      // If this throws, the catch below runs and deleteDoc is never reached, so
      // the drawing survives. That order is the whole guarantee: a delete with
      // no record is exactly what the log exists to prevent, and the only way
      // to be sure of it is to make the record a precondition rather than a
      // follow-up. The reverse order — delete, then log — loses the record
      // permanently on any failure, and loses the SNAPSHOT along with it,
      // because by then there is nothing left to photograph.
      //
      // The user sees "Could not delete" and the drawing is still there. That
      // is the honest outcome: nothing happened, and they can try again.
      const { id, ...fields } = deleteTarget;
      await logDeletion({
        entityType: 'shopDrawing',
        entityId: id,
        label: deleteTarget.drawingNumber || '(no number)',
        detail: deleteTarget.customerName || '',
        snapshot: fields,
        deletedBy: userName,
      });

      await deleteDoc(doc(db, 'shopDrawings', deleteTarget.id));
      // Dropped from the list in place — no reload, so the list does not flash
      // and lose its scroll position or the search the user had typed.
      setDrawings(prev => prev.filter(x => x.id !== deleteTarget.id));
      // If the editor is open on the drawing that just stopped existing, there
      // is nothing left to edit. Back to the list.
      if (record?.id === deleteTarget.id) {
        setRecord(null);
        setForm(BLANK);
        setView('list');
      }
      setDeleteTarget(null);
    } catch (err) {
      // Covers BOTH steps. If the log write failed the drawing is untouched and
      // this message is literally true; if the delete itself failed it is true
      // as well, at the cost of a log entry for a deletion that did not happen.
      // That asymmetry is deliberate — an extra log line is noise, a missing one
      // is the failure mode being guarded against.
      console.error('Error deleting shop drawing:', err);
      alert('Could not delete the drawing — nothing was removed. Check the connection and try again.');
    }
    setDeleting(false);
  };

  const openNew = () => {
    setRecord(null);
    setForm(BLANK);
    setCustomerSearch('');
    setDocSearch('');
    setDocLookup({ state: 'idle', matches: [] });
    setView('editor');
  };

  // useCallback so the deep-link effect below can depend on it without
  // re-running on every render. Only stable setters are used, so no deps.
  const openExisting = useCallback((d) => {
    setRecord(d);
    setForm({
      type: d.type || BLANK.type,
      // Older records — every gate — have no plates field at all. plateForForm
      // carries a length recorded under the retired numeric field into the free
      // text one, so opening and re-saving an older plate drawing cannot drop it.
      plates: Array.isArray(d.plates) ? d.plates.map(plateForForm) : [],
      grade: d.grade || BLANK.grade,
      openingIn: String(d.openingIn ?? ''),
      heightIn: String(d.heightIn ?? ''),
      // Drawings saved before quantity existed have no field; they are one gate.
      quantity: String(clampQuantity(d.quantity ?? 1)),
      // Older records predate these flags and have no field — default to false.
      barbedWire: !!d.barbedWire,
      actualSize: !!d.actualSize,
      // Drawings saved before frame only or nailers existed have no field.
      frameOnly: !!d.frameOnly,
      nailers2x4: !!d.nailers2x4,
      // Absent on every drawing saved before the field existed, which must keep
      // all three boards — so only an explicit false turns it off.
      nailerMidRail: d.nailerMidRail !== false,
      horizontalRailCount: Number.isFinite(d.horizontalRailCount) ? String(d.horizontalRailCount) : '',
      verticalMemberCount: Number.isFinite(d.verticalMemberCount) ? String(d.verticalMemberCount) : '',
      fullHeight: !!d.fullHeight,
      blankSheet: !!d.blankSheet,
      // Drawings saved before customer copy existed have no field at all.
      customerCopy: !!d.customerCopy,
      // Cantilever-only fields; older records predate them entirely.
      slideDirection: d.slideDirection || BLANK.slideDirection,
      hangSide: d.hangSide || BLANK.hangSide,
      operation: d.operation || BLANK.operation,
      // Drawings saved before this field existed have none: they print the
      // computed overall length, exactly as a new drawing does.
      overallLengthIn: d.overallLengthIn != null ? String(d.overallLengthIn) : '',
      // ── AN OLDER OVERRIDE WITH NO BASE ──
      // Saved before the base existed, so what the formula was when the number
      // was typed is unknowable. The formula AS IT STANDS ON OPENING is adopted
      // as the base: no warning now — there is nothing anyone could act on —
      // and any change made from here on is caught. The adopted base is
      // written with the next save.
      overallLengthBaseIn: d.overallLengthIn == null
        ? ''
        : String(d.overallLengthBaseIn != null
          ? d.overallLengthBaseIn
          : formulaOverallIn({ type: d.type || BLANK.type, openingIn: d.openingIn })),
      frameFinish: d.frameFinish || BLANK.frameFinish,
      meshFinish: d.meshFinish || BLANK.meshFinish,
      meshGauge: d.meshGauge || '',
      customerId: d.customerId ?? null,
      customerName: d.customerName || '',
      documentRef: d.documentRef || '',
      // Older drawings have none of these; they keep showing documentRef.
      linkedDocId: d.linkedDocId || null,
      linkedDocType: d.linkedDocType || null,
      linkedDocNumber: d.linkedDocNumber || '',
    });
    setCustomerSearch('');
    setView('editor');
  }, []);

  // ── Deep link from elsewhere in the app ──
  // CustomerProfile navigates here with state { drawingId }. Open that drawing
  // in the editor, taking exactly the path a row click takes.
  //
  // The attempt is deliberately held until the list has finished loading —
  // matching against a still-empty `drawings` would fall through to the list
  // view and the link would appear to do nothing. A missing id (deleted
  // drawing, stale link) also stays on the list rather than opening a blank
  // editor. Either way the state is cleared afterwards, so navigating Back and
  // returning to this route does not force the editor open a second time.
  const consumedIdRef = useRef(null);
  const requestedId = location.state?.drawingId || null;

  useEffect(() => {
    if (!requestedId || consumedIdRef.current === requestedId) return;
    if (loading) return; // wait for the list before deciding
    consumedIdRef.current = requestedId;

    const match = drawings.find(d => d.id === requestedId);
    if (match) openExisting(match);

    navigate(location.pathname, { replace: true, state: null });
  }, [requestedId, loading, drawings, openExisting, navigate, location.pathname]);

  // ── POST PLATES ──
  // A different DRAWING on the same sheet: the gate fields mean nothing here,
  // so the form replaces them with the plate list rather than leaving them
  // sitting there inert.
  //
  // Numbers are kept as TYPED in the form (so a half-typed "0." is not rewritten
  // under the cursor) and normalised here on the way to the drawing and to
  // Firestore — the same pattern the gate fields already use. Defined above
  // buildPayload because that is its first reader.
  const isPlates = form.type === PLATE_TYPE;
  const normalizedPlates = (Array.isArray(form.plates) ? form.plates : []).map((p, i) => ({
    id: p.id ?? `plate-${i}`,
    plateSizeIn: parseFloat(p.plateSizeIn) || 0,
    thicknessIn: parseFloat(p.thicknessIn) || 0,
    postOdIn: parseFloat(p.postOdIn) || 0,
    // Free text, stored verbatim. Trimmed at the ends ONLY — nothing inside the
    // string is touched, so 10'6" and 7'-3" survive exactly as typed. Capped as
    // a backstop to the input's own limit. '' means "not recorded", and the
    // drawing then prints no length line rather than an empty label.
    postLength: String(p.postLength ?? '').trim().slice(0, POST_LENGTH_MAX),
    // The four retired corner positions collapse to Offset here, so an old
    // drawing re-saved from this screen stores one of the two that now exist.
    position: normalizePlatePosition(p.position),
    quantity: clampQuantity(p.quantity),
  }));

  // Only the inputs — never the computed cuts, never the SVG.
  const buildPayload = () => ({
    type: form.type,
    // ONLY on a plate drawing. A gate's saved record is byte-for-byte what it
    // always was — no empty plates array written where one never existed.
    ...(isPlates ? { plates: normalizedPlates } : {}),
    grade: form.grade,
    openingIn: parseFloat(form.openingIn) || 0,
    heightIn: parseFloat(form.heightIn) || 0,
    quantity: clampQuantity(form.quantity),
    barbedWire: !!form.barbedWire,
    actualSize: !!form.actualSize,
    frameOnly: !!form.frameOnly,
    nailers2x4: !!form.nailers2x4,
    nailerMidRail: form.nailerMidRail !== false,
    // null means "use the rule", which is what every existing drawing stores by
    // having no field at all.
    horizontalRailCount: countOverrideForSave(form.horizontalRailCount),
    verticalMemberCount: countOverrideForSave(form.verticalMemberCount),
    fullHeight: !!form.fullHeight,
    blankSheet: !!form.blankSheet,
    customerCopy: !!form.customerCopy,
    slideDirection: form.slideDirection,
    hangSide: form.hangSide,
    operation: form.operation,
    // null unless a cantilever carries a real override. A swing gate has no
    // such field on screen, so nothing hidden is saved for it.
    overallLengthIn: form.type === 'Cantilever' ? overallOverrideIn(form.overallLengthIn) : null,
    // Stored with the override and only with it: null whenever the override is.
    overallLengthBaseIn: form.type === 'Cantilever' && overallOverrideIn(form.overallLengthIn) !== null
      ? (Number.isFinite(parseFloat(form.overallLengthBaseIn)) ? parseFloat(form.overallLengthBaseIn) : formulaOverallIn(form))
      : null,
    frameFinish: form.frameFinish,
    meshFinish: form.meshFinish,
    meshGauge: form.meshGauge,
    customerId: form.customerId || null,
    customerName: form.customerName || '',
    documentRef: form.documentRef.trim() || null,
    // The resolved link. Null when nothing is attached — never a half-set of
    // fields, because unlinkDocument clears all three together.
    linkedDocId: form.linkedDocId || null,
    linkedDocType: form.linkedDocId ? form.linkedDocType : null,
    linkedDocNumber: form.linkedDocId ? form.linkedDocNumber : '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      if (record?.id) {
        // Existing drawing — leave drawingNumber, createdAt, revision and status
        // alone; only the inputs and the edit trail change.
        const update = { ...buildPayload(), lastEditedBy: drawnBy, lastEditedAt: serverTimestamp() };
        await updateDoc(doc(db, 'shopDrawings', record.id), update);
        setRecord(prev => ({ ...prev, ...update }));
      } else {
        const drawingNumber = await generateShopDrawingNumber();
        const ref = await addDoc(collection(db, 'shopDrawings'), {
          ...buildPayload(),
          drawingNumber,
          drawnBy,
          revision: 'A',   // groundwork for the approval flow — no UI yet
          status: 'Draft', // groundwork for the approval flow — no UI yet
          // Fabrication status. Written once at creation so a new drawing is
          // explicitly Pending rather than merely missing the field; the shop
          // screen owns every change after this.
          shopStatus: 'Pending',
          shopStatusBy: null,
          shopStatusAt: null,
          createdAt: serverTimestamp(),
          issuedAt: null,
          lastEditedBy: drawnBy,
          lastEditedAt: serverTimestamp(),
        });
        // Read back so createdAt is the RESOLVED server value — the title block
        // must show the stored time, not the local clock.
        const saved = await getDoc(ref);
        setRecord({ id: ref.id, ...saved.data() });
      }
      await loadDrawings();
    } catch (err) {
      console.error('Error saving shop drawing:', err);
      alert('Error saving drawing: ' + err.message);
    }
    setSaving(false);
  };

  // ── Title block timestamp ──
  // A saved drawing shows the date and time it was CREATED, frozen; an unsaved
  // draft shows the current clock.
  const createdDate = toDate(record?.createdAt);
  const stamp = createdDate || new Date();

  const gate = {
    type: form.type,
    // Read by GateSheet only when the type is Post Plates.
    plates: normalizedPlates,
    grade: form.grade,
    openingIn: parseFloat(form.openingIn) || 0,
    heightIn: parseFloat(form.heightIn) || 0,
    quantity: clampQuantity(form.quantity),
    barbedWire: !!form.barbedWire,
    actualSize: !!form.actualSize,
    frameOnly: !!form.frameOnly,
    nailers2x4: !!form.nailers2x4,
    nailerMidRail: form.nailerMidRail !== false,
    horizontalRailCount: countOverrideForSave(form.horizontalRailCount),
    verticalMemberCount: countOverrideForSave(form.verticalMemberCount),
    fullHeight: !!form.fullHeight,
    customerCopy: !!form.customerCopy,
    slideDirection: form.slideDirection,
    hangSide: form.hangSide,
    operation: form.operation,
    // Label only — computeGateCuts ignores it on a swing gate and never lets it
    // reach the geometry on a cantilever.
    overallLengthIn: overallOverrideIn(form.overallLengthIn),
  };

  const meta = {
    drawingNumber: record?.drawingNumber || '',
    customerName: form.customerName,
    frameFinish: form.frameFinish,
    meshFinish: form.meshFinish,
    meshGauge: form.meshGauge,
    dateText: fmtDate(stamp),
    timeText: fmtTime(stamp),
    drawnBy: record?.drawnBy || drawnBy,
    documentRef: form.documentRef.trim() || null,
  };

  const cuts = computeGateCuts(gate);
  const yesNo = (b) => (b ? 'yes' : 'no');

  // ── Print gating ──
  // An unsaved draft has no drawing number, so a printed sheet would carry no
  // identity. And a gate wide enough to need diagonals is missing a structural
  // member (the diagonal placeholder in GateElevation draws nothing), so that
  // sheet must not reach a welder at all.
  // A blank sheet is a hand-filled template: it carries no drawing number to
  // require, and no dimensions for the missing diagonals to contradict — so
  // neither gate applies and it prints straight away.
  const isBlank = !!form.blankSheet;
  // Customer copy hides shop-only detail (frame tube sizes, the cantilever cut
  // callout) and adds a signature block. It never changes a cut.
  const isCustomerCopy = !!form.customerCopy;
  // ── POST PLATES ──
  // A different DRAWING on the same sheet: the gate fields mean nothing here,
  // so they are replaced by the plate list rather than sitting there inert.
  const isCantilever = form.type === 'Cantilever';
  // Actual size is a swing-gate concept — a cantilever always uses its formulas,
  // so the checkbox is hidden and the flag ignored there.
  const isSwing = !isCantilever;
  const actualSize = isSwing && !!form.actualSize;
  // Frame only is a swing concept too — a cantilever always carries mesh.
  const frameOnly = isSwing && !!form.frameOnly;
  // Nailers hang off frame only, so the checkbox appears and applies only there.
  const nailers2x4 = frameOnly && !!form.nailers2x4;
  // Full height is only offered where it would actually do something: a short
  // swing gate that is not already taking its height verbatim.
  const isShort = (parseFloat(form.heightIn) || 0) <= SHORT_GATE_MAX_HEIGHT_IN;
  const fullHeight = isSwing && isShort && !actualSize && !!form.fullHeight;
  const isSaved = !!record?.id;
  const canPrint = isBlank || isSaved;
  const printTitle = isBlank
    ? 'Print this blank template (use your browser’s print dialog to save as PDF)'
    : !isSaved
      ? 'Save the drawing first — an unsaved draft has no drawing number'
        : 'Print this sheet (use your browser’s print dialog to save as PDF)';

  const handlePrint = () => window.print();

  const searchDigits = normalizePhone(customerSearch);
  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (searchDigits.length > 0 && getAllPhoneNumbers(c).some(n => normalizePhone(n).includes(searchDigits))) ||
    c.customerNumber?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Filtered view of the list. `drawings` itself is never re-sorted or mutated,
  // so clearing the box restores the full list in its original newest-first
  // order for free.
  const trimmedSearch = search.trim();
  const filteredDrawings = useMemo(
    () => (trimmedSearch ? drawings.filter(d => drawingMatchesSearch(d, trimmedSearch)) : drawings),
    [drawings, trimmedSearch],
  );

  // On a blank sheet the numeric fields no longer reach the drawing, so they are
  // DIMMED rather than removed or disabled — unchecking restores everything
  // exactly as it was, and nothing the user typed is lost.
  const dimmed = { opacity: 0.45 };

  // ── Plate list ──
  //
  // Numbers are kept as TYPED in the form (so a half-typed "0." is not
  // rewritten under the cursor) and normalised on the way to the drawing and to
  // Firestore, the same way the gate fields already work.
  const setPlates = (next) => set('plates', next);
  const addPlate = () => {
    const list = Array.isArray(form.plates) ? form.plates : [];
    // The cap is the drawing's, not a preference: past six the squares and
    // their four lines of text stop being readable. See MAX_PLATES_PER_SHEET.
    if (list.length >= MAX_PLATES_PER_SHEET) return;
    setPlates([...list, { ...BLANK_PLATE, id: `plate-${Date.now()}` }]);
  };
  const updatePlate = (index, key, value) =>
    setPlates((form.plates || []).map((p, i) => (i === index ? { ...p, [key]: value } : p)));
  const removePlate = (index) => setPlates((form.plates || []).filter((_, i) => i !== index));
  const movePlate = (index, delta) => {
    const list = [...(form.plates || [])];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setPlates(list);
  };

  const textField = (label, key, extra = {}, dim = false) => (
    <div style={dim ? dimmed : undefined}>
      <label style={labelStyle}>{label}</label>
      <input
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        onFocus={focusOn}
        onBlur={focusOff}
        style={fieldStyle}
        {...extra}
      />
    </div>
  );

  const selectField = (label, key, options, dim = false) => (
    <div style={dim ? dimmed : undefined}>
      <label style={labelStyle}>{label}</label>
      <select
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        onFocus={focusOn}
        onBlur={focusOff}
        style={fieldStyle}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <>
      {/* Print rules. Scoped to this route because the <style> only exists
          while this page is mounted. Printing outputs the sheet and nothing
          else: the sidebar (an <aside>, and the only one on this page), the
          header, the form and the list are all removed, and the page's
          sidebar offset and card chrome are stripped so the sheet sits
          alone on the paper. Landscape suits the 850x620 sheet. */}
      <style>{`
        @media print {
          @page { size: landscape; margin: 0.4in; }
          aside { display: none !important; }
          /* The header row sets display:flex INLINE, and Tailwind's
             .print\\:hidden is a plain "display:none" with no !important — an
             inline declaration beats it, so the header printed anyway. (The
             cards hid correctly because their styles never set display.) Same
             shape of fix as ViewDocument's .screen-only-actions rule: an
             explicit !important, which does outrank an inline style. */
          .sd-header { display: none !important; }
          /* min-height:100vh is set inline and resolves to a full page box in
             print, so the wrapper reserved a whole page on its own. */
          .sd-page { margin-left: 0 !important; background: #FFFFFF !important; min-height: 0 !important; }
          .sd-shell { max-width: none !important; padding: 0 !important; }
          .sd-editor-grid { display: block !important; }
          .sd-sheet-card { border: none !important; padding: 0 !important; border-radius: 0 !important; break-inside: avoid; }
        }
      `}</style>
      <Sidebar />
      <div className="sd-page" style={{ minHeight: '100vh', background: C_BG, marginLeft: SIDEBAR_W, fontFamily: 'Manrope, sans-serif' }}>
        <div className="sd-shell" style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>

          <div className="sd-header print:hidden" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ color: C_TEXT, fontSize: 24, fontWeight: 800, margin: '0 0 4px 0' }}>📐 Shop Drawings</h1>
              <p style={{ color: C_TEXT_2, fontSize: 13, margin: 0 }}>
                {view === 'list'
                  ? 'Saved drawings, newest first'
                  : (record?.drawingNumber ? `Editing ${record.drawingNumber}` : 'New drawing — not yet saved')}
              </p>
            </div>
            {view === 'list' ? (
              <button onClick={openNew} style={btnPrimary}>+ New Drawing</button>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setView('list'); }} style={btnPlain}>← Back to List</button>
                {/* Shop status — ADMINS ONLY, and only on a SAVED drawing:
                    there is no record to move until one exists. Deliberately
                    quiet — a small labelled select sitting with the secondary
                    controls, never a row of buttons competing with Save. */}
                {isAdmin && record?.id && (() => {
                  const st = shopStatusOf(record);
                  return (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C_TEXT_2 }}>
                      Shop status
                      <select
                        value={resolveShopStatus(record)}
                        disabled={statusSavingId === record.id}
                        onChange={e => changeShopStatus(record, e.target.value)}
                        title={record.shopStatusBy ? `${st.label} by ${record.shopStatusBy}` : st.label}
                        style={{
                          background: st.bg, color: st.fg,
                          border: `1px solid ${st.fg}33`, borderRadius: 6,
                          fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
                          padding: '4px 6px', fontFamily: 'inherit',
                          cursor: statusSavingId === record.id ? 'default' : 'pointer',
                          opacity: statusSavingId === record.id ? 0.6 : 1,
                        }}
                      >
                        {SHOP_STATUS_KEYS.map(key => (
                          <option key={key} value={key} style={{ background: '#FFFFFF', color: C_TEXT }}>
                            {SHOP_STATUS[key].icon} {SHOP_STATUS[key].label}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })()}
                {/* Only on a SAVED drawing — there is nothing to delete until
                    something exists — and only for an admin. Placed first in
                    the group, furthest from Save, and outlined rather than
                    filled so it never reads as the obvious next tap. */}
                {isAdmin && record?.id && (
                  <button
                    onClick={() => requestDelete(record)}
                    style={btnDanger}
                    onMouseEnter={e => { e.currentTarget.style.background = C_DANGER_TINT; e.currentTarget.style.borderColor = C_DANGER; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = `${C_DANGER}40`; }}
                  >
                    🗑 Delete
                  </button>
                )}
                <button
                  onClick={handlePrint}
                  disabled={!canPrint}
                  title={printTitle}
                  style={{ ...btnPlain, opacity: canPrint ? 1 : 0.5, cursor: canPrint ? 'pointer' : 'not-allowed' }}
                >
                  🖨️ Print
                </button>
                <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1, cursor: saving ? 'default' : 'pointer' }}>
                  {saving ? 'Saving…' : '💾 Save Drawing'}
                </button>
              </div>
            )}
          </div>

          {/* ── LIST ── */}
          {/* The search box belongs to the LIST only — it sits inside this
              view === 'list' block, so switching to the editor takes it away
              with the rest of the list. */}
          {view === 'list' && (
            <div className="print:hidden" style={cardStyle}>
              {loading ? (
                <p style={{ color: C_TEXT_2, fontSize: 14, margin: 0 }}>Loading…</p>
              ) : drawings.length === 0 ? (
                <p style={{ color: C_TEXT_2, fontSize: 14, margin: 0 }}>No drawings yet. Click “New Drawing” to create one.</p>
              ) : (
                <>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                  <div style={{ flex: 1, position: 'relative', minWidth: 260 }}>
                    <input
                      type="text"
                      placeholder="Search by drawing number, customer, document or gate type..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      style={searchInputStyle}
                      onFocus={focusOn}
                      onBlur={focusOff}
                    />
                    <span style={{
                      position: 'absolute', left: 16, top: '50%',
                      transform: 'translateY(-50%)',
                      color: C_TEXT_2, fontSize: 16, pointerEvents: 'none',
                    }}>🔍</span>
                  </div>
                  {/* Count only while a query is active — with the box empty the
                      list speaks for itself. */}
                  {trimmedSearch && (
                    <span style={{ color: C_TEXT_2, fontSize: 13, whiteSpace: 'nowrap' }}>
                      {filteredDrawings.length} of {drawings.length} drawings
                    </span>
                  )}
                </div>
                {filteredDrawings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                    <p style={{ color: C_TEXT_2, fontSize: 14, margin: '0 0 10px 0' }}>No drawings match that search.</p>
                    <button onClick={() => setSearch('')} style={btnPlain}>Clear search</button>
                  </div>
                ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: C_TINT, color: C_TEXT }}>
                        <th className="px-3 py-2 text-left">Drawing #</th>
                        <th className="px-3 py-2 text-left">Customer</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Size</th>
                        <th className="px-3 py-2 text-left">Qty</th>
                        <th className="px-3 py-2 text-left">Shop</th>
                        <th className="px-3 py-2 text-left">Drawn By</th>
                        <th className="px-3 py-2 text-left">Date</th>
                        {/* No heading text: a column of icon buttons does not
                            need one, and a word here would give the action more
                            presence than it should have. */}
                        {isAdmin && <th className="px-3 py-2" style={{ width: 44 }} aria-label="Delete" />}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDrawings.map((d, i) => (
                        <tr
                          key={d.id}
                          onClick={() => openExisting(d)}
                          style={{ backgroundColor: i % 2 === 0 ? C_CARD : '#FAFCFE', borderTop: `1px solid ${C_BORDER}`, cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = C_TINT; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = i % 2 === 0 ? C_CARD : '#FAFCFE'; }}
                        >
                          <td className="px-3 py-2 font-bold" style={{ color: C_ACCENT, fontFamily: 'monospace' }}>{d.drawingNumber}</td>
                          <td className="px-3 py-2" style={{ color: C_TEXT }}>{d.customerName || '—'}</td>
                          <td className="px-3 py-2" style={{ color: C_TEXT }}>{d.type}</td>
                          {/* Through the shared formatter, so a stored 41.25
                              cannot render differently here than on the sheet. */}
                          <td className="px-3 py-2" style={{ color: C_TEXT }}>{formatInches(d.openingIn)} × {formatInches(d.heightIn)}</td>
                          {/* Bolded above one so a multi-gate job is visible
                              without opening the drawing. */}
                          <td className="px-3 py-2" style={{
                            color: clampQuantity(d.quantity ?? 1) > 1 ? C_ACCENT : C_TEXT_2,
                            fontWeight: clampQuantity(d.quantity ?? 1) > 1 ? 700 : 400,
                          }}>
                            {clampQuantity(d.quantity ?? 1)}
                          </td>
                          {/* Set by the shop, and changeable here by an admin.
                              Colour AND text AND a glyph, so it survives a bad
                              monitor.

                              The admin's version is a small select carrying the
                              same colours — secondary by design, nothing like
                              the tablet's big buttons. Everyone else sees the
                              badge exactly as before.

                              stopPropagation because the whole ROW opens the
                              drawing: without it, choosing a status would open
                              the editor underneath the menu. */}
                          <td className="px-3 py-2" onClick={e => { if (isAdmin) e.stopPropagation(); }}>
                            {isAdmin ? (() => {
                              const st = shopStatusOf(d);
                              return (
                                <select
                                  value={resolveShopStatus(d)}
                                  disabled={statusSavingId === d.id}
                                  onChange={e => changeShopStatus(d, e.target.value)}
                                  title={d.shopStatusBy ? `${st.label} by ${d.shopStatusBy}` : st.label}
                                  aria-label={`Shop status for ${d.drawingNumber || 'this drawing'}`}
                                  style={{
                                    background: st.bg, color: st.fg,
                                    border: `1px solid ${st.fg}33`, borderRadius: 6,
                                    fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
                                    padding: '2px 6px', fontFamily: 'inherit',
                                    cursor: statusSavingId === d.id ? 'default' : 'pointer',
                                    opacity: statusSavingId === d.id ? 0.6 : 1,
                                  }}
                                >
                                  {SHOP_STATUS_KEYS.map(key => (
                                    <option key={key} value={key} style={{ background: '#FFFFFF', color: C_TEXT }}>
                                      {SHOP_STATUS[key].icon} {SHOP_STATUS[key].label}
                                    </option>
                                  ))}
                                </select>
                              );
                            })() : (() => {
                              const st = shopStatusOf(d);
                              return (
                                <span
                                  title={d.shopStatusBy ? `${st.label} by ${d.shopStatusBy}` : st.label}
                                  style={{
                                    display: 'inline-block',
                                    background: st.bg, color: st.fg,
                                    fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
                                    padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
                                  }}
                                >
                                  {st.icon} {st.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-2" style={{ color: C_TEXT_2 }}>{d.drawnBy || '—'}</td>
                          <td className="px-3 py-2" style={{ color: C_TEXT_2 }}>{fmtDate(toDate(d.createdAt)) || '—'}</td>
                          {/* Delete — ADMINS ONLY, and hidden entirely from
                              anyone else rather than shown disabled: a control
                              you can never use is just clutter.

                              stopPropagation because the whole ROW opens the
                              drawing. Without it, aiming at the bin would open
                              the editor underneath the confirmation. */}
                          {isAdmin && (
                            <td className="px-3 py-2" style={{ textAlign: 'center' }}>
                              <button
                                onClick={e => { e.stopPropagation(); requestDelete(d); }}
                                title={`Delete ${d.drawingNumber} permanently`}
                                aria-label={`Delete ${d.drawingNumber}`}
                                style={{
                                  background: 'transparent', border: 'none', cursor: 'pointer',
                                  color: C_TEXT_2, fontSize: 14, lineHeight: 1,
                                  padding: '4px 6px', borderRadius: 6, fontFamily: 'inherit',
                                  transition: 'background 0.15s, color 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = C_DANGER_TINT; e.currentTarget.style.color = C_DANGER; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C_TEXT_2; }}
                              >
                                🗑
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
                </>
              )}
            </div>
          )}

          {/* ── EDITOR ── */}
          {view === 'editor' && (
            <div className="sd-editor-grid grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

              <div className="print:hidden" style={cardStyle}>
                <p style={sectionTitle}>Customer</p>
                <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 20 }}>
                  {/* Customer picker — same search fields the document pages use,
                      storing customerId with customerName alongside it. */}
                  <div className="col-span-2" style={{ position: 'relative' }}>
                    <label style={labelStyle}>Customer</label>
                    {form.customerId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input value={form.customerName} readOnly style={{ ...readOnlyStyle, color: C_TEXT }} />
                        <button
                          onClick={() => { set('customerId', null); set('customerName', ''); setCustomerSearch(''); }}
                          style={{ ...btnPlain, padding: '8px 14px', flexShrink: 0 }}
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          value={customerSearch}
                          onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                          onFocus={e => { setShowCustomerDropdown(true); focusOn(e); }}
                          onBlur={focusOff}
                          placeholder="Search by name, phone, or customer number…"
                          style={fieldStyle}
                        />
                        {showCustomerDropdown && customerSearch && (
                          <div style={{
                            position: 'absolute', zIndex: 100, top: '100%', left: 0, right: 0,
                            background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 12,
                            maxHeight: 240, overflowY: 'auto', boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)',
                          }}>
                            {filteredCustomers.length === 0 ? (
                              <p style={{ padding: '12px 16px', color: C_TEXT_2, margin: 0, fontSize: 13 }}>No customers found.</p>
                            ) : filteredCustomers.slice(0, 25).map(c => (
                              <button
                                key={c.id}
                                onClick={() => {
                                  set('customerId', c.id);
                                  set('customerName', c.name || '');
                                  setShowCustomerDropdown(false);
                                  setCustomerSearch('');
                                }}
                                style={{
                                  display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
                                  border: 'none', borderBottom: `1px solid ${C_BORDER}`, padding: '10px 14px',
                                  cursor: 'pointer', fontFamily: 'inherit',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = C_TINT; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                <span style={{ color: C_TEXT, fontWeight: 600, fontSize: 14 }}>{c.name}</span>
                                <span style={{ color: C_ACCENT, fontFamily: 'monospace', fontSize: 12, marginLeft: 8 }}>{c.customerNumber}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* ── LINKED DOCUMENT ──────────────────────────────
                      A REAL reference: the document's id, its type and its
                      number, resolved by looking the number up before it is
                      saved.

                      "Related document" below it is the OLD free-text field,
                      kept and untouched. It is not migrated because it cannot
                      be: "Quote #26-001256", "see inv 1256" and "Bob's job" are
                      all in there, and guessing which of those resolves to what
                      would attach drawings to documents nobody chose. Old
                      drawings keep showing what somebody typed; new ones get a
                      link that works. */}
                  <div className="col-span-2">
                    <label style={labelStyle}>Linked quote or invoice</label>

                    {form.linkedDocId ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        border: `1px solid ${C_BORDER}`, borderRadius: 10,
                        padding: '8px 12px', background: C_TINT,
                      }}>
                        <span style={{
                          fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                          padding: '2px 8px', borderRadius: 6,
                          background: form.linkedDocType === 'quote' ? '#FDF1DD' : '#E6F7EE',
                          color: form.linkedDocType === 'quote' ? '#854F0B' : '#0F6E56',
                        }}>
                          {form.linkedDocType === 'quote' ? 'QUOTE' : 'INVOICE'}
                        </span>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: C_TEXT, fontSize: 14 }}>
                          {form.linkedDocNumber}
                        </span>
                        <button
                          type="button"
                          onClick={unlinkDocument}
                          style={{
                            marginLeft: 'auto', background: 'transparent', border: 'none',
                            color: C_TEXT_2, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          ✕ Remove link
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          value={docSearch}
                          onChange={e => setDocSearch(e.target.value)}
                          onFocus={focusOn}
                          onBlur={focusOff}
                          placeholder="Type a number — 26-001256, 001256 or 1256"
                          aria-label="Find a quote or invoice"
                          style={fieldStyle}
                        />

                        {/* WHAT WAS MATCHED, BEFORE IT IS SAVED. The number
                            alone is not enough to be sure: the same number can
                            be a quote AND an invoice, so the type and the
                            customer are shown and the user picks. */}
                        {docLookup.state === 'searching' && (
                          <p style={{ color: C_TEXT_2, fontSize: 12, margin: '6px 0 0 0' }}>Searching…</p>
                        )}

                        {docLookup.state === 'none' && (
                          // SAID OUT LOUD, not silently dropped. Nothing is
                          // stored, and the user is told why rather than
                          // discovering later that the link never took.
                          <p style={{ color: '#A32D2D', fontSize: 12, margin: '6px 0 0 0' }}>
                            No quote or invoice found for “{docSearch.trim()}”. Nothing has been linked.
                          </p>
                        )}

                        {docLookup.state === 'found' && (
                          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {docLookup.matches.length > 1 && (
                              <p style={{ color: '#854F0B', fontSize: 12, margin: 0 }}>
                                That number is both a quote and an invoice. Choose which one this drawing is for.
                              </p>
                            )}
                            {docLookup.matches.map(m => (
                              <button
                                key={m.docType + m.id}
                                type="button"
                                onClick={() => linkDocument(m)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                                  textAlign: 'left', width: '100%',
                                  border: `1px solid ${C_BORDER}`, borderRadius: 10,
                                  padding: '8px 12px', background: C_CARD,
                                  cursor: 'pointer', fontFamily: 'inherit',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = C_TINT; }}
                                onMouseLeave={e => { e.currentTarget.style.background = C_CARD; }}
                              >
                                <span style={{
                                  fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
                                  padding: '2px 8px', borderRadius: 6,
                                  background: m.docType === 'quote' ? '#FDF1DD' : '#E6F7EE',
                                  color: m.docType === 'quote' ? '#854F0B' : '#0F6E56',
                                }}>
                                  {m.docType === 'quote' ? 'QUOTE' : 'INVOICE'}
                                </span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: C_TEXT, fontSize: 14 }}>
                                  {m.docNumber}
                                </span>
                                <span style={{ color: C_TEXT_2, fontSize: 13 }}>{m.customerName || '—'}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="col-span-2">
                    {textField('Related document (note)', 'documentRef', { placeholder: 'Free text — kept for older drawings' })}
                  </div>

                  {/* Assigned by the app on save, not entered here. */}
                  <div>
                    <label style={refLabelStyle}>Drawing number</label>
                    <input
                      value={record?.drawingNumber || 'will be assigned on save'}
                      readOnly
                      style={refFieldStyle}
                    />
                  </div>
                  <div>
                    <label style={refLabelStyle}>Drawn by</label>
                    <input value={record?.drawnBy || drawnBy} readOnly style={refFieldStyle} />
                  </div>
                </div>

                <p style={sectionTitle}>{isPlates ? 'Post Plates' : 'Gate'}</p>
                <div className="grid grid-cols-2 gap-3" style={{ marginBottom: isPlates ? 12 : 20 }}>
                  {selectField('Type', 'type', ['Single Swing', 'Double Swing', 'Cantilever', PLATE_TYPE])}
                  {!isPlates && selectField('Grade', 'grade', ['Residential', 'Commercial'])}
                  {/* Relabelled under actual size so it is obvious the figures
                      typed here are cut lengths, not an opening to deduct from. */}
                  {!isPlates && textField(actualSize ? 'Rail cut (in)' : 'Opening (in)', 'openingIn', { type: 'number', min: '0' }, isBlank)}
                  {!isPlates && textField(actualSize ? 'Upright cut (in)' : 'Height (in)', 'heightIn', { type: 'number', min: '0' }, isBlank)}
                  {/* Whole gates only. The input refuses fractions and anything
                      below one; clampQuantity catches whatever still gets
                      through (a pasted value, an emptied field) on the way to
                      the drawing and to Firestore. A plate drawing counts each
                      plate separately instead, in the list below. */}
                  {!isPlates && textField('Quantity', 'quantity', { type: 'number', min: '1', step: '1' })}
                  {/* Cantilever-only — hidden entirely for swing gates. */}
                  {isCantilever && selectField('Slide direction', 'slideDirection', ['Right', 'Left'])}
                  {isCantilever && selectField('Hang side', 'hangSide', ['Outside', 'Inside'])}
                  {isCantilever && selectField('Operation', 'operation', ['Manual', 'Automatic'])}

                  {/* ── OVERALL LENGTH — cantilever only ──
                      Shows the computed total until somebody types over it. A
                      typed figure changes the NUMBER printed above the frame and
                      nothing else.

                      Right after typing, the form confirms the override QUIETLY —
                      it is in effect, it is not a problem. The warning appears
                      only when the formula has moved since the number was typed
                      (see overallLengthBaseIn), naming the typed figure and the
                      new formula, with a one-tap reset. The typed figure is never
                      silently replaced, and never silently kept against a formula
                      that has changed under it. All of this lives in the form, not
                      on the sheet: the shop gets one clean number. */}
                  {isCantilever && (() => {
                    const typed = String(form.overallLengthIn ?? '').trim() !== '';
                    const override = overallOverrideIn(form.overallLengthIn);
                    const computedLabel = cuts.computedOverallLengthLabel;
                    const computedValue = String(Math.round(cuts.totalLengthIn * 100) / 100);
                    // Clears the override AND its base — set() clears the base
                    // whenever the override goes.
                    const useFormula = () => { setOverallDraft(null); set('overallLengthIn', ''); };
                    const baseIn = parseFloat(form.overallLengthBaseIn);
                    // No base (should not happen once a drawing is open — see
                    // openExisting) means no warning, never a guessed one.
                    const formulaMoved = override !== null
                      && Number.isFinite(baseIn)
                      && !sameToTheCent(baseIn, cuts.totalLengthIn);
                    // "Keep 28'-0"": the typed figure stands against the formula
                    // as it is now, which is exactly what re-typing it would do.
                    const keepTyped = () => setForm(f => ({ ...f, overallLengthBaseIn: String(formulaOverallIn(f)) }));
                    const helper = { color: C_TEXT_2, fontSize: 12, margin: '6px 0 0 0' };
                    const warnBox = {
                      marginTop: 8, padding: '10px 12px', borderRadius: 10,
                      background: '#FDF1DD', border: '1px solid #E6C998', color: '#854F0B', fontSize: 13,
                    };
                    const linkBtn = {
                      background: 'none', border: 'none', padding: 0, color: C_ACCENT,
                      fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
                    };
                    return (
                      <div className="col-span-2" style={isBlank ? dimmed : undefined}>
                        <label htmlFor="overallLengthIn" style={labelStyle}>Overall length (in)</label>
                        <input
                          id="overallLengthIn"
                          type="number"
                          min="0"
                          step="any"
                          value={overallDraft ?? (typed ? form.overallLengthIn : computedValue)}
                          onFocus={e => { focusOn(e); setOverallDraft(e.target.value); }}
                          onChange={e => { setOverallDraft(e.target.value); set('overallLengthIn', e.target.value); }}
                          onBlur={e => { focusOff(e); setOverallDraft(null); }}
                          style={fieldStyle}
                        />

                        {!typed && (
                          <p style={helper}>
                            Prints above the frame as {computedLabel}, from the formula (opening + counterbalance).
                            Type a different figure to print that instead — the frame itself does not change.
                          </p>
                        )}

                        {typed && override === null && (
                          <div role="alert" style={warnBox}>
                            ⚠️ “{form.overallLengthIn}” is not a length, so the sheet prints the formula’s {computedLabel}.{' '}
                            <button type="button" onClick={useFormula} style={linkBtn}>Use the formula</button>
                          </div>
                        )}

                        {/* Override in effect, formula unchanged since it was
                            typed: a plain note, deliberately not styled as a
                            warning and not an alert. */}
                        {override !== null && !formulaMoved && (
                          <p style={helper}>
                            {cuts.overallLengthDiverges
                              ? <>Printing {cuts.overallLengthLabel} instead of the formula’s {computedLabel}. Only the label changes; the frame follows the formula.</>
                              : <>Printing {cuts.overallLengthLabel}, typed — the same as the formula.</>}{' '}
                            <button type="button" onClick={useFormula} style={linkBtn}>Use the formula instead</button>
                          </p>
                        )}

                        {formulaMoved && (
                          <div role="alert" style={warnBox}>
                            <p style={{ margin: 0, fontWeight: 700 }}>
                              ⚠️ Overall length is set to {cuts.overallLengthLabel} but the formula gives {computedLabel}.
                            </p>
                            <p style={{ margin: '4px 0 8px 0', fontSize: 12 }}>
                              The formula was {formatFeetInches(baseIn)} when the override was set; the opening has changed since.
                              The sheet still prints {cuts.overallLengthLabel}.
                            </p>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={useFormula}
                                style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12 }}
                              >
                                Reset to {computedLabel}
                              </button>
                              <button
                                type="button"
                                onClick={keepTyped}
                                style={{
                                  background: '#FFFFFF', border: '1px solid #E6C998', color: '#854F0B',
                                  padding: '6px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                                  cursor: 'pointer', fontFamily: 'inherit',
                                }}
                              >
                                Keep {cuts.overallLengthLabel}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* ── THE PLATE LIST ──
                    Replaces the gate fields entirely when the type is Post
                    Plates. Each row is one plate SIZE with its own quantity;
                    the sheet draws them in this order, so ▲▼ is the layout. */}
                {isPlates && (
                  <div style={{ marginBottom: 20 }}>
                    {(form.plates || []).length === 0 && (
                      <p style={{ color: C_TEXT_2, fontSize: 13, margin: '0 0 10px 0' }}>
                        No plates yet. Add one for each post size on this order.
                      </p>
                    )}
                    {(form.plates || []).map((p, i) => (
                      <div key={p.id ?? i} style={{
                        border: `1px solid ${C_BORDER}`, borderRadius: 12, padding: 12, marginBottom: 10, background: C_BG,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ color: C_TEXT, fontWeight: 700, fontSize: 13 }}>Plate {i + 1}</span>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button type="button" onClick={() => movePlate(i, -1)} disabled={i === 0}
                              title="Move up" style={{ ...btnPlain, padding: '4px 8px', fontSize: 12, opacity: i === 0 ? 0.4 : 1 }}>▲</button>
                            <button type="button" onClick={() => movePlate(i, 1)} disabled={i === (form.plates || []).length - 1}
                              title="Move down" style={{ ...btnPlain, padding: '4px 8px', fontSize: 12, opacity: i === (form.plates || []).length - 1 ? 0.4 : 1 }}>▼</button>
                            <button type="button" onClick={() => removePlate(i)}
                              title="Remove this plate" style={{ ...btnPlain, padding: '4px 8px', fontSize: 12, color: C_DANGER, borderColor: `${C_DANGER}40` }}>✕</button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            {/* "Plate size", never just "size" — the other
                                measurement on this row is the POST diameter.
                                Stocked sizes only; a drawing holding something
                                else keeps it, marked "(saved)", until re-picked. */}
                            <label style={labelStyle}>Plate size</label>
                            <select value={String(p.plateSizeIn)}
                              onChange={e => updatePlate(i, 'plateSizeIn', parseFloat(e.target.value))}
                              onFocus={focusOn} onBlur={focusOff} style={fieldStyle}>
                              {optionsFor(PLATE_SIZE_OPTIONS, p.plateSizeIn, plateSizeLabel).map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={labelStyle}>Thickness</label>
                            <select value={String(p.thicknessIn)}
                              onChange={e => updatePlate(i, 'thicknessIn', parseFloat(e.target.value))}
                              onFocus={focusOn} onBlur={focusOff} style={fieldStyle}>
                              {optionsFor(THICKNESS_OPTIONS, p.thicknessIn, thicknessLabel).map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label style={labelStyle}>Post diameter</label>
                            <select value={String(p.postOdIn)}
                              onChange={e => updatePlate(i, 'postOdIn', parseFloat(e.target.value))}
                              onFocus={focusOn} onBlur={focusOff} style={fieldStyle}>
                              {optionsFor(POST_OD_OPTIONS, p.postOdIn, postOdLabel).map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            {/* FREE TEXT. A real length is 10'6" — a foot mark
                                and an inch mark in one value — which no numeric
                                field can hold. Typed exactly, printed exactly;
                                the only change is a trim on save. Empty is
                                fine and prints no length line at all. */}
                            <label style={labelStyle}>Post length</label>
                            <input type="text" value={p.postLength ?? ''}
                              maxLength={POST_LENGTH_MAX}
                              placeholder={`10'6" · 8' · 96" · 7'-3"`}
                              onChange={e => updatePlate(i, 'postLength', e.target.value)}
                              onFocus={focusOn} onBlur={focusOff} style={fieldStyle} />
                          </div>
                          <div>
                            <label style={labelStyle}>Post position</label>
                            <select value={normalizePlatePosition(p.position)}
                              onChange={e => updatePlate(i, 'position', e.target.value)}
                              onFocus={focusOn} onBlur={focusOff} style={fieldStyle}>
                              {PLATE_POSITIONS.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                          <div>
                            <label style={labelStyle}>Quantity</label>
                            <input type="number" min="1" step="1" value={p.quantity}
                              onChange={e => updatePlate(i, 'quantity', e.target.value)}
                              onFocus={focusOn} onBlur={focusOff} style={fieldStyle} />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button type="button" onClick={addPlate}
                      disabled={(form.plates || []).length >= MAX_PLATES_PER_SHEET}
                      style={{ ...btnPlain, opacity: (form.plates || []).length >= MAX_PLATES_PER_SHEET ? 0.5 : 1 }}>
                      + Add Plate
                    </button>
                    {(form.plates || []).length >= MAX_PLATES_PER_SHEET && (
                      <p style={{ color: C_TEXT_2, fontSize: 12, margin: '8px 0 0 0' }}>
                        {MAX_PLATES_PER_SHEET} plates is the most that stays readable on one sheet.
                        For more, save this drawing and create a second one — it gets its own number and prints alongside.
                      </p>
                    )}
                  </div>
                )}

                <p style={sectionTitle}>Options</p>
                <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 20 }}>
                  {/* Gate-only options. A plate has no mesh, no barbed wire and no
                      cut deductions to skip — only its finish carries over. */}
                  {!isPlates && (<>
                  <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="barbedWire"
                      checked={!!form.barbedWire}
                      onChange={e => set('barbedWire', e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                    />
                    <label htmlFor="barbedWire" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                      Barbed wire (3 strands, +12&quot; uprights)
                    </label>
                  </div>
                  {isSwing && (
                    <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        id="actualSize"
                        checked={actualSize}
                        onChange={e => set('actualSize', e.target.checked)}
                        style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                      />
                      <label htmlFor="actualSize" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                        Actual size (no deductions)
                      </label>
                    </div>
                  )}
                  {/* Full height — short swing gates only, and pointless under
                      actual size, which already takes the height verbatim. */}
                  {isSwing && isShort && !actualSize && (
                    <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        id="fullHeight"
                        checked={fullHeight}
                        onChange={e => set('fullHeight', e.target.checked)}
                        style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                      />
                      <label htmlFor="fullHeight" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                        Full height (no 1.5&quot; deduction)
                      </label>
                    </div>
                  )}
                  {/* Frame only — swing gates just as actual size is. Every cut
                      stays exactly the same; the mesh is simply not installed. */}
                  {isSwing && (
                    <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type="checkbox"
                        id="frameOnly"
                        checked={frameOnly}
                        // Turning frame only OFF clears the nailers with it.
                        // The nailer checkbox disappears at the same moment, so
                        // leaving it set would strand a true the user can no
                        // longer see or reach.
                        onChange={e => {
                          const on = e.target.checked;
                          setForm(f => ({ ...f, frameOnly: on, nailers2x4: on ? f.nailers2x4 : false }));
                        }}
                        style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                      />
                      <label htmlFor="frameOnly" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                        Frame only (no mesh)
                      </label>
                    </div>
                  )}
                  {/* Nailers only make sense on a bare frame, so the option is
                      not offered until Frame only is on. */}
                  {isSwing && frameOnly && (
                    <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 24 }}>
                      <input
                        type="checkbox"
                        id="nailers2x4"
                        checked={nailers2x4}
                        onChange={e => set('nailers2x4', e.target.checked)}
                        style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                      />
                      <label htmlFor="nailers2x4" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                        2x4 nailers attached
                      </label>
                    </div>
                  )}
                  {/* The MID RAIL's board. Offered only where it does something:
                      nailers on, and an intermediate rail to put one on. Wood
                      wants all three boards, PVC only top and bottom — so this
                      defaults ON and unticking removes just that board. */}
                  {isSwing && nailers2x4 && cuts.hasMidRail && (
                    <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 48 }}>
                      <input
                        type="checkbox"
                        id="nailerMidRail"
                        checked={form.nailerMidRail !== false}
                        onChange={e => set('nailerMidRail', e.target.checked)}
                        style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                      />
                      <label htmlFor="nailerMidRail" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                        Nailer on the mid rail
                        <span style={{ color: C_TEXT_2, fontWeight: 500 }}> — untick for PVC (top and bottom only)</span>
                      </label>
                    </div>
                  )}
                  </>)}
                </div>

                {/* ── MEMBER COUNTS ──
                    The rules decide; these let the office ask for something
                    else, in either direction, without changing the rules. Each
                    shows the rule's number as its starting point and offers one
                    tap back to it. Swing gates only — a cantilever's members
                    come from its bay layout.
                    DIAGONALS ARE NOT HERE: they follow the width rule, which is
                    a structural decision and not the office's to take. */}
                {isSwing && !isPlates && (
                  <>
                    <p style={sectionTitle}>Members</p>
                    <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 20 }}>
                      {[
                        { key: 'horizontalRailCount', label: 'Horizontal rails', rule: cuts.horizontalRuleCount, max: 6 },
                        { key: 'verticalMemberCount', label: 'Vertical members', rule: cuts.verticalRuleCount, max: 4 },
                      ].map(({ key, label, rule, max }) => {
                        const overridden = String(form[key] ?? '') !== '';
                        return (
                          <div key={key}>
                            <label htmlFor={key} style={labelStyle}>
                              {label}: {overridden ? form[key] : `${rule} (rule)`}
                            </label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                id={key}
                                type="number"
                                min="0"
                                max={String(max)}
                                step="1"
                                value={form[key] ?? ''}
                                placeholder={`${rule} (rule)`}
                                onChange={e => set(key, e.target.value)}
                                onFocus={focusOn}
                                onBlur={focusOff}
                                style={fieldStyle}
                              />
                              {overridden && (
                                <button
                                  type="button"
                                  onClick={() => set(key, '')}
                                  title={`Back to the rule's ${rule}`}
                                  style={{ ...btnPlain, whiteSpace: 'nowrap' }}
                                >
                                  ↺ Rule
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {cuts.membersOverridden && (
                        <p className="col-span-2" style={{ color: C_TEXT_2, fontSize: 12, margin: 0 }}>
                          Both leaves of a double get the same count. Cut lengths do not change — an extra
                          rail is another piece at {cuts.railCutLabel}, an extra vertical another at{' '}
                          {cuts.verticalCutLabel}. The sheet says the counts were overridden.
                        </p>
                      )}
                    </div>
                  </>
                )}

                <p style={sectionTitle}>Finishes</p>
                <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 20 }}>
                  {/* The finish applies to a plate as much as to a frame, so it
                      stays for both. Mesh does not exist on a plate. */}
                  {selectField('Frame finish', 'frameFinish', ['Galvanized', 'Black'])}
                  {/* Frame only DIMS the two mesh fields rather than disabling
                      or clearing them — unchecking restores whatever was typed,
                      the same way the numeric fields behave on a blank sheet. */}
                  {!isPlates && selectField('Mesh finish', 'meshFinish', ['Galvanized', 'Black'], frameOnly)}
                  {!isPlates && textField('Mesh gauge', 'meshGauge', { placeholder: '11.5' }, isBlank || frameOnly)}
                </div>

                <p style={sectionTitle}>Sheet</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="blankSheet"
                      checked={isBlank}
                      onChange={e => set('blankSheet', e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                    />
                    <label htmlFor="blankSheet" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                      Blank sheet (write measurements by hand)
                    </label>
                  </div>
                  {/* Customer copy is independent of blank sheet — either, both,
                      or neither. The preview below redraws as it is toggled, so
                      the user sees the signed version before printing it. */}
                  <div className="col-span-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      id="customerCopy"
                      checked={isCustomerCopy}
                      onChange={e => set('customerCopy', e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: C_ACCENT }}
                    />
                    <label htmlFor="customerCopy" style={{ color: C_TEXT, fontWeight: 600, fontSize: 13 }}>
                      Customer copy (for signature)
                    </label>
                  </div>
                </div>

                {/* Read-only view of what the fabrication rules decided. */}
                <div style={{ marginTop: 20, paddingTop: 14, borderTop: `1px solid ${C_BORDER}` }}>
                  <p style={{ color: C_TEXT_2, fontSize: 12, margin: 0, lineHeight: 1.7 }}>
                    {cuts.quantity > 1 && <>Quantity: {cuts.quantity} gates — cut sizes are per gate<br /></>}
                    {cuts.actualSize && <>Actual size: yes — no deductions applied<br /></>}
                    {cuts.fullHeight && !cuts.actualSize && (
                      <>Full height: yes — 1.5&quot; deduction skipped<br /></>
                    )}
                    {cuts.frameOnly && <>Frame only: yes — no mesh, cuts unchanged<br /></>}
                    {cuts.nailers2x4 && (
                      <>2x4 nailers: yes — top{cuts.hasMidRail ? ', mid' : ''} and bottom rail<br /></>
                    )}
                    Rail cut: {cuts.railCutLabel}<br />
                    Upright cut: {cuts.uprightCutLabel}<br />
                    Mid rail: {yesNo(cuts.hasMidRail)}<br />
                    Vertical mid rails: {cuts.verticalMidRailCount}<br />
                    Diagonals: {yesNo(cuts.hasDiagonals)}<br />
                    Barbed wire: {cuts.barbedWire ? `yes — ${cuts.barbStrandCount} strand, +${formatInches(cuts.barbExtensionIn)} upright` : 'no'}
                    {isCantilever && (
                      <>
                        <br />
                        {/* Formatted, not interpolated raw: a half-inch opening
                            makes these fractional, and a float artefact would
                            otherwise print in full. */}
                        Counterbalance: {formatInches(cuts.counterbalanceIn)}{cuts.counterbalanceIn === 144 ? ' (capped at 12 ft)' : ''}<br />
                        Total length: {formatInches(cuts.totalLengthIn)}<br />
                        Overall length printed: {cuts.overallLengthLabel}
                        {cuts.overallLengthOverrideIn !== null ? ' (typed — label only)' : ' (formula)'}<br />
                        Drawn opening: {formatInches(cuts.drawnOpeningIn)} ({cuts.drawnOpeningLabel})<br />
                        End upright: {cuts.endUprightLabel}<br />
                        {/* Bay spacing was rounded to one decimal inline — the
                            same one-decimal bug as the rail cut, in a spot the
                            engine's formatter never reached. */}
                        Opening bays: {cuts.openingBayCount} @ {formatInches(cuts.openingBaySpacingIn)}<br />
                        Counterbalance bays: {cuts.cbBayCount} @ {formatInches(cuts.cbBaySpacingIn)}<br />
                        Mesh: {cuts.meshCoversCounterbalance ? 'whole gate (UL325)' : 'opening only'}<br />
                        Barb coverage: opening only<br />
                        Barb arm lean: {cuts.barbLeanDirection}{cuts.hangSide === 'Inside' ? ' (far face)' : ''}
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div>
                <div className="sd-sheet-card" style={cardStyle}>
                  <GateSheet gate={gate} meta={meta} blankSheet={isBlank} />
                </div>
              </div>

            </div>
          )}

        </div>

        {/* ── DELETE CONFIRMATION ──────────────────────────────────────
            A MODAL, not window.confirm — the app already has this exact
            pattern in ViewDocument (red ⚠️ heading, the document and customer
            named in the sentence, "cannot be undone", a red Delete Permanently
            against a neutral Cancel), and matching it means a delete looks the
            same wherever it happens.

            What it does NOT copy is ViewDocument's password step. That guards a
            financial record with payments against it; a drawing is a set of
            measurements that can be typed again in a minute. A password there
            and not here is a considered difference, not an oversight.

            It names the drawing number and the customer, says the deletion is
            permanent, and says the drawing will also leave the customer's
            record — because that is where somebody will look for it next and
            not find it. */}
        {deleteTarget && (
          <div
            className="print:hidden"
            style={{
              position: 'fixed', inset: 0, zIndex: 60,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
              background: 'rgba(3,4,94,0.5)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Delete drawing"
          >
            <div style={{
              background: '#FFFFFF', border: `1px solid ${C_BORDER}`, borderRadius: 16,
              padding: 24, maxWidth: 460, width: '100%',
              boxShadow: '0 8px 24px rgba(3,4,94,0.15)',
            }}>
              <h3 style={{ color: C_DANGER, fontSize: 19, fontWeight: 800, margin: '0 0 10px 0' }}>
                ⚠️ Delete Drawing
              </h3>
              <p style={{ color: C_TEXT_2, fontSize: 14, lineHeight: 1.5, margin: '0 0 10px 0' }}>
                You are about to permanently delete{' '}
                <strong style={{ color: C_TEXT, fontFamily: 'monospace' }}>{deleteTarget.drawingNumber}</strong>
                {' '}for <strong style={{ color: C_TEXT }}>{deleteTarget.customerName || 'no customer'}</strong>.
              </p>
              <p style={{ color: C_TEXT_2, fontSize: 14, lineHeight: 1.5, margin: '0 0 6px 0' }}>
                <strong style={{ color: C_DANGER }}>This cannot be undone.</strong>{' '}
                There is no archive and nothing to restore from.
              </p>
              <p style={{ color: C_TEXT_2, fontSize: 14, lineHeight: 1.5, margin: '0 0 18px 0' }}>
                It will also disappear from{' '}
                {deleteTarget.customerName ? `${deleteTarget.customerName}'s` : 'the customer\u2019s'} record,
                which reads the same list.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={confirmDelete}
                  disabled={deleting}
                  style={{
                    flex: 1, background: C_DANGER, border: 'none', color: '#FFFFFF',
                    padding: '10px 18px', borderRadius: 10,
                    fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                    cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.6 : 1,
                  }}
                >
                  {deleting ? 'Deleting…' : 'Delete Permanently'}
                </button>
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  style={{ ...btnPlain, flex: 1, background: C_TINT, border: 'none', color: C_TEXT_2 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
